// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DonutChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import PieChartOptionsBuilder from './Izenda.PieChartOptionsBuilder';
import { map, includes, get, flow, has, split, head, toNumber, reduce, find, isNil } from 'lodash/fp';
import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import { exportClass } from 'share/Izenda.ServiceProvider';

@exportClass('DonutChartOptionsBuilder')
export default class DonutChartOptionsBuilder extends PieChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   */
  buildOptionsByType(visualType: string, userOptions, dataParser): Object {
    const pieOptions = super.buildOptionsByType(visualType, userOptions, dataParser);

    const izendaOptions = userOptions.izendaOptions || {};
    const isShowDataLabel = get('plotOptions.series.dataLabels.enabled', pieOptions);

    // to reduce the size of ring if those values are shown
    const pieSize = flow(
      get('plotOptions.pie.size'),
      split('%'),
      head,
      toNumber
    )(pieOptions);
    const pieInnerSize = has('plotOptions.pie.innerSize', pieOptions) ? Math.round(pieSize / 3) : 0;

    let series = getSeries(userOptions.series, pieSize, pieInnerSize);

    if (izendaOptions.bottomXpercent) {
      series = applyBottomPercent(izendaOptions, series, this.DefaultColors);
    }

    return $izenda.extend(true, pieOptions, {
      chart: {
        type: 'pie',
        events: {
          click: null
        }
      },
      plotOptions: {
        series: {
          point: {
            events: {
              click: null
            }
          }
        },
        pie: {
          innerSize: pieInnerSize ? pieInnerSize + '%' : 0
        }
      },
      legend: {
        labelFormatter() {
          const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
          const { labelName, nameField } = get('series.userOptions', this);
          const fieldDataType = get('reportPartElm.field.dataFieldType', nameField);
          const reportPartElm = get('reportPartElm', nameField);
          const valueStringFormated = HighchartUtils.getFormatedValueByStringFormat(reportPartElm, this.originalNameValue, fieldDataType);
          const fieldValues = {
            value: isNil(valueStringFormated) ? this.name : valueStringFormated,
            originalValue: this.originalNameValue
          };

          return dataParser.getFormatData(
            fieldValues,
            izendaSeriesConfig[labelName]
          );
        }
      },
      series
    });

    function getSeries(series: any, pieSize: number, innerRingSize: number) {
      if (!series) {
        return [];
      }

      const serieName = series.serieName;
      const yAxisField = series.yAxisField;
      const serieData = series.data;

      const numSeries = serieData.length;
      const ringSize = Math.floor((pieSize - innerRingSize) / numSeries);
      let innerSize;

      const donutSeries = serieData.map((drilldownLevel: any, idx: number) => {
        innerSize = innerRingSize + ringSize * idx;
        return {
          name: serieName,
          data: drilldownLevel.data,
          separatorField: series.separatorField,
          separatorValue: series.separatorValue,
          labelName: get('nameField.fieldNameAlias', drilldownLevel),
          nameField: drilldownLevel.nameField,
          yAxisField,

          innerSize: innerSize + '%',
          size: innerSize + ringSize + '%',
          dataLabels: {
            enabled: isShowDataLabel && idx === numSeries - 1
          }
        };
      });

      return donutSeries;
    }

    function applyBottomPercent(izendaOptions: any, series: Array<any>, defaultColors: Object) {
      const topLevel = head(series);
      const {
        colors,
        plotOptions: {
          series: { colorByPoint }
        },
        izDisableMultiColor
      } = userOptions;

      const serieColors = colors || defaultColors;
      const topData = ChartDataParser.processDataByBottomPercent(
        izendaOptions.bottomXpercent,
        topLevel.data,
        'name',
        'y'
      );

      let drilldownData;
      series[0].data = topData.newData.map((item, index) => {
        const colorIndex = colorByPoint && !izDisableMultiColor ? index % serieColors.length : 0;
        item.color = serieColors[colorIndex];
        return item;
      });

      if (topData.otherItems && series.length > 1) {
        for (var idx = 1; idx < series.length; idx++) {
          drilldownData = series[idx];
          drilldownData.data = removeItemByGroup(drilldownData.data, topData.otherItems);
          drilldownData.data.push(topData.otherItem);
        }
      }

      return series;

      function removeItemByGroup(source: any, removedItems: any) {
        const groupIds = map(item => item.groupId.id, removedItems);

        return reduce(
          (result, item) => {
            if (!includes(item.groupId.id, groupIds)) {
              const groupItem = find(i => i.groupId.id === item.groupId.id, series[0].data) || {};
              return [...result, { ...item, color: groupItem.color }];
            }
            return result;
          },
          [],
          source
        );
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.DonutChartOptionsBuilder.js